import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { InputWithAlert } from '@/components/ui/input-with-alert';
import { DatePicker } from '@/components/ui/date-picker';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { toast } from '@/components/ui/use-toast';
import { Loader } from '@/components/ProtectedRoute';
import { 
  getKwestionariushByClientId, 
  createKwestionariush, 
  updateKwestionariush 
} from '@/service/weexpertService';

export function KwestionariushTab({ userData }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [edit, setEdit] = useState(false);
  const [inputChecked, setInputChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Personal Information fields
  const [birthDate, setBirthDate] = useState(null);
  const [birthPlace, setBirthPlace] = useState('');
  const [registrationAddress, setRegistrationAddress] = useState('');
  const [taxResidenceAddress, setTaxResidenceAddress] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [taxOffice, setTaxOffice] = useState('');
  const [nfzBranch, setNfzBranch] = useState('');

  // Employment Declaration
  const [isEmployed, setIsEmployed] = useState(false);
  const [employmentPeriodFrom, setEmploymentPeriodFrom] = useState(null);
  const [employmentPeriodTo, setEmploymentPeriodTo] = useState(null);
  const [employmentUnlimited, setEmploymentUnlimited] = useState(false);
  const [salaryLevel, setSalaryLevel] = useState('');
  const [onLeave, setOnLeave] = useState(false);
  const [leavePeriodFrom, setLeavePeriodFrom] = useState(null);
  const [leavePeriodTo, setLeavePeriodTo] = useState(null);

  // Other Contract Declaration
  const [hasOtherContract, setHasOtherContract] = useState(false);
  const [otherContractFrom, setOtherContractFrom] = useState(null);
  const [otherContractTo, setOtherContractTo] = useState(null);
  const [otherContractExceedsMinimum, setOtherContractExceedsMinimum] = useState(false);

  // Other Insurance Declaration
  const [hasOtherInsurance, setHasOtherInsurance] = useState(false);
  const [otherInsuranceTitle, setOtherInsuranceTitle] = useState('');

  // Pension Declaration
  const [isPensioner, setIsPensioner] = useState(false);
  const [pensionDecisionNumber, setPensionDecisionNumber] = useState('');

  // Disability Declaration
  const [hasDisability, setHasDisability] = useState(false);
  const [disabilityLevel, setDisabilityLevel] = useState('');
  const [disabilityFrom, setDisabilityFrom] = useState(null);
  const [disabilityTo, setDisabilityTo] = useState(null);

  // Other Declarations
  const [isStudent, setIsStudent] = useState(false);
  const [isUnemployed, setIsUnemployed] = useState(false);
  const [hasSocialInsurance, setHasSocialInsurance] = useState(false);

  // Voluntary Insurance
  const [wantsVoluntaryHealthInsurance, setWantsVoluntaryHealthInsurance] = useState(false);
  const [wantsVoluntaryPensionInsurance, setWantsVoluntaryPensionInsurance] = useState(false);

  // Tax Declaration
  const [wantsTaxExemption, setWantsTaxExemption] = useState(false);

  // Tax Residency Certificate
  const [hasTaxResidencyCertificate, setHasTaxResidencyCertificate] = useState(false);
  const [taxResidencyCertificateFrom, setTaxResidencyCertificateFrom] = useState(null);
  const [taxResidencyCertificateTo, setTaxResidencyCertificateTo] = useState(null);

  // Author Costs
  const [exceedsAuthorCostLimit, setExceedsAuthorCostLimit] = useState(false);
  const [authorCostAmount, setAuthorCostAmount] = useState('');

  // Data Processing Consent
  const [dataProcessingConsent, setDataProcessingConsent] = useState(false);

  // Fetch kwestionariush data
  const { data: kwestionariushData, isLoading } = useQuery({
    queryKey: ['kwestionariush', userData?.id],
    queryFn: () => getKwestionariushByClientId(userData?.id),
    enabled: !!userData?.id,
    retry: false,
    refetchOnWindowFocus: false,
    onSuccess: (response) => {
      if (response.data) {
        const data = response.data.data;
        populateFormData(data);
      }
    },
    onError: (error) => {
      // 404 means no kwestionariush exists yet, which is normal
      if (error.response?.status !== 404) {
        console.error('Failed to fetch kwestionariush:', error);
      }
    }
  });

  const populateFormData = (data) => {
    if (!data) return;

    // Personal Info
    const personalInfo = data.personalInfo;
    if (personalInfo) {
      setBirthDate(personalInfo.birthDate ? new Date(personalInfo.birthDate) : null);
      setBirthPlace(personalInfo.birthPlace || '');
      setRegistrationAddress(personalInfo.registrationAddress || '');
      setTaxResidenceAddress(personalInfo.taxResidenceAddress || '');
      setBankAccountNumber(personalInfo.bankAccountNumber || '');
      setTaxOffice(personalInfo.taxOffice || '');
      setNfzBranch(personalInfo.nfzBranch || '');
    }

    // Declarations
    const declarations = data.declarations;
    if (declarations) {
      // Employment
      const employment = declarations.employment;
      if (employment) {
        setIsEmployed(employment.isEmployed || false);
        setEmploymentPeriodFrom(employment.period?.from ? new Date(employment.period.from) : null);
        setEmploymentPeriodTo(employment.period?.to ? new Date(employment.period.to) : null);
        setEmploymentUnlimited(employment.period?.isUnlimited || false);
        setSalaryLevel(employment.salaryLevel || '');
        setOnLeave(employment.onLeave || false);
        setLeavePeriodFrom(employment.leavePeriod?.from ? new Date(employment.leavePeriod.from) : null);
        setLeavePeriodTo(employment.leavePeriod?.to ? new Date(employment.leavePeriod.to) : null);
      }

      // Other Contract
      const otherContract = declarations.otherContract;
      if (otherContract) {
        setHasOtherContract(otherContract.hasOtherContract || false);
        setOtherContractFrom(otherContract.period?.from ? new Date(otherContract.period.from) : null);
        setOtherContractTo(otherContract.period?.to ? new Date(otherContract.period.to) : null);
        setOtherContractExceedsMinimum(otherContract.exceedsMinimum || false);
      }

      // Other Insurance
      const otherInsurance = declarations.otherInsurance;
      if (otherInsurance) {
        setHasOtherInsurance(otherInsurance.hasOtherInsurance || false);
        setOtherInsuranceTitle(otherInsurance.title || '');
      }

      // Pension
      const pension = declarations.pension;
      if (pension) {
        setIsPensioner(pension.isPensioner || false);
        setPensionDecisionNumber(pension.decisionNumber || '');
      }

      // Disability
      const disability = declarations.disability;
      if (disability) {
        setHasDisability(disability.hasDisability || false);
        setDisabilityLevel(disability.level || '');
        setDisabilityFrom(disability.period?.from ? new Date(disability.period.from) : null);
        setDisabilityTo(disability.period?.to ? new Date(disability.period.to) : null);
      }

      // Simple boolean declarations
      setIsStudent(declarations.studentStatus || false);
      setIsUnemployed(declarations.unemploymentStatus || false);
      setHasSocialInsurance(declarations.socialInsuranceStatus || false);

      // Voluntary Insurance
      const voluntaryInsurance = declarations.voluntaryInsurance;
      if (voluntaryInsurance) {
        setWantsVoluntaryHealthInsurance(voluntaryInsurance.health || false);
        setWantsVoluntaryPensionInsurance(voluntaryInsurance.pension || false);
      }

      // Tax
      setWantsTaxExemption(declarations.taxExemption || false);

      // Tax Residency Certificate
      const taxResidencyCertificate = declarations.taxResidencyCertificate;
      if (taxResidencyCertificate) {
        setHasTaxResidencyCertificate(taxResidencyCertificate.hasCertificate || false);
        setTaxResidencyCertificateFrom(taxResidencyCertificate.period?.from ? new Date(taxResidencyCertificate.period.from) : null);
        setTaxResidencyCertificateTo(taxResidencyCertificate.period?.to ? new Date(taxResidencyCertificate.period.to) : null);
      }

      // Author Costs
      const authorCosts = declarations.authorCosts;
      if (authorCosts) {
        setExceedsAuthorCostLimit(authorCosts.exceedsLimit || false);
        setAuthorCostAmount(authorCosts.amount || '');
      }

      // Data Processing Consent
      setDataProcessingConsent(declarations.dataProcessingConsent || false);
    }
  };

  // Employment validation helpers
  const getEmploymentFromError = () => {
    return inputChecked && isEmployed && !employmentPeriodFrom;
  };

  const getEmploymentToError = () => {
    return inputChecked && isEmployed && !employmentUnlimited && !employmentPeriodTo;
  };

  const validateForm = () => {
    setInputChecked(true);
    
    // Basic required fields validation
    if (!birthPlace || !registrationAddress || !taxResidenceAddress || 
        !bankAccountNumber || !taxOffice || !nfzBranch) {
      return false;
    }

    // Data processing consent is required
    if (!dataProcessingConsent) {
      return false;
    }

    // Employment validation
    if (isEmployed) {
      // Always require start date when employed
      if (!employmentPeriodFrom) {
        return false;
      }
      
      // If not unlimited, require end date
      if (!employmentUnlimited && !employmentPeriodTo) {
        return false;
      }
      
      // If unlimited is checked, ensure no end date
      if (employmentUnlimited && employmentPeriodTo) {
        return false;
      }
    }

    // Conditional validations
    if (hasOtherContract && (!otherContractFrom || !otherContractTo)) {
      return false;
    }

    if (hasOtherInsurance && !otherInsuranceTitle) {
      return false;
    }

    if (isPensioner && !pensionDecisionNumber) {
      return false;
    }

    if (hasDisability && (!disabilityLevel || !disabilityFrom || !disabilityTo)) {
      return false;
    }

    if (hasTaxResidencyCertificate && (!taxResidencyCertificateFrom || !taxResidencyCertificateTo)) {
      return false;
    }

    if (exceedsAuthorCostLimit && !authorCostAmount) {
      return false;
    }

    return true;
  };

  const prepareFormData = () => {
    return {
      personalInfo: {
        birthDate: birthDate ? format(birthDate, 'yyyy-MM-dd') : null,
        birthPlace,
        registrationAddress,
        taxResidenceAddress,
        bankAccountNumber,
        taxOffice,
        nfzBranch
      },
      declarations: {
        employment: {
          isEmployed,
          period: isEmployed ? {
            from: employmentPeriodFrom ? format(employmentPeriodFrom, 'yyyy-MM-dd') : null,
            to: employmentUnlimited ? null : (employmentPeriodTo ? format(employmentPeriodTo, 'yyyy-MM-dd') : null),
            isUnlimited: employmentUnlimited
          } : null,
          salaryLevel: isEmployed ? salaryLevel : null,
          onLeave,
          leavePeriod: onLeave ? {
            from: leavePeriodFrom ? format(leavePeriodFrom, 'yyyy-MM-dd') : null,
            to: leavePeriodTo ? format(leavePeriodTo, 'yyyy-MM-dd') : null
          } : null
        },
        otherContract: {
          hasOtherContract,
          period: hasOtherContract ? {
            from: otherContractFrom ? format(otherContractFrom, 'yyyy-MM-dd') : null,
            to: otherContractTo ? format(otherContractTo, 'yyyy-MM-dd') : null
          } : null,
          exceedsMinimum: hasOtherContract ? otherContractExceedsMinimum : undefined
        },
        otherInsurance: {
          hasOtherInsurance,
          title: hasOtherInsurance ? otherInsuranceTitle : null
        },
        pension: {
          isPensioner,
          decisionNumber: isPensioner ? pensionDecisionNumber : null
        },
        disability: {
          hasDisability,
          level: hasDisability ? disabilityLevel : null,
          period: hasDisability ? {
            from: disabilityFrom ? format(disabilityFrom, 'yyyy-MM-dd') : null,
            to: disabilityTo ? format(disabilityTo, 'yyyy-MM-dd') : null
          } : null
        },
        studentStatus: isStudent,
        unemploymentStatus: isUnemployed,
        socialInsuranceStatus: hasSocialInsurance,
        voluntaryInsurance: {
          health: wantsVoluntaryHealthInsurance,
          pension: wantsVoluntaryPensionInsurance
        },
        taxExemption: wantsTaxExemption,
        taxResidencyCertificate: {
          hasCertificate: hasTaxResidencyCertificate,
          period: hasTaxResidencyCertificate ? {
            from: taxResidencyCertificateFrom ? format(taxResidencyCertificateFrom, 'yyyy-MM-dd') : null,
            to: taxResidencyCertificateTo ? format(taxResidencyCertificateTo, 'yyyy-MM-dd') : null
          } : null
        },
        authorCosts: {
          exceedsLimit: exceedsAuthorCostLimit,
          amount: exceedsAuthorCostLimit ? authorCostAmount : null
        },
        dataProcessingConsent
      }
    };
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast({
        title: t('questionnaire:validation_error_title'),
        description: t('questionnaire:validation_error_message'),
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const formData = prepareFormData();
      const isUpdate = kwestionariushData?.data;

      if (isUpdate) {
        await updateKwestionariush(kwestionariushData.data.id, { data: formData });
        toast({
          title: t('questionnaire:success_title'),
          description: t('questionnaire:questionnaire_updated')
        });
      } else {
        await createKwestionariush({
          clientId: userData.id,
          data: formData
        });
        toast({
          title: t('questionnaire:success_title'),
          description: t('questionnaire:questionnaire_created')
        });
      }

      // Refresh data
      queryClient.invalidateQueries(['kwestionariush', userData.id]);
      queryClient.invalidateQueries(['userMe']);
      setEdit(false);
      setInputChecked(false);
    } catch (error) {
      console.error('Failed to save kwestionariush:', error);
      toast({
        title: t('questionnaire:error_title'),
        description: t('questionnaire:save_error_message'),
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasKwestionariush = kwestionariushData?.data;

  if (isLoading) {
    return <Loader className="mx-auto" />;
  }

  return (
    <div className="space-y-6">
      <h3>{t('questionnaire:title')}</h3>
      
      {!hasKwestionariush && !edit ? (
        <div className="text-center py-8">
          <p className="text-gray-500 mb-4">{t('questionnaire:no_questionnaire_message')}</p>
          <Button onClick={() => setEdit(true)}>
            {t('questionnaire:fill_questionnaire_button')}
          </Button>
        </div>
      ) : (
        <>
          {/* Personal Information Section */}
          <div className="space-y-0">
            <h4 className="font-semibold pb-4">{t('questionnaire:personal_info_title')}</h4>
            <div className='pb-4'>
            <DatePicker
              label={t('questionnaire:birth_date_label')}
              value={birthDate}
              date={birthDate}
              setDate={setBirthDate}
              disabled={!edit}
              error={inputChecked && !birthDate}
              errorMessage={t('questionnaire:birth_date_required')}
              placeholder={t('questionnaire:birth_date_placeholder')}
            />
            </div>
            <div className='pb-4'>
            <InputWithAlert
              label={t('questionnaire:birth_place_label')}
              value={birthPlace}
              onChange={(e) => setBirthPlace(e.target.value)}
              disabled={!edit}
              error={inputChecked && !birthPlace}
              errorMessage={t('questionnaire:birth_place_required')}
              placeholder={t('questionnaire:birth_place_placeholder')}
            />
            </div>
            <div className='pb-4'>
            <InputWithAlert
              label={t('questionnaire:registration_address_label')}
              value={registrationAddress}
              onChange={(e) => setRegistrationAddress(e.target.value)}
              disabled={!edit}
              error={inputChecked && !registrationAddress}
              errorMessage={t('questionnaire:registration_address_required')}
              placeholder={t('questionnaire:registration_address_placeholder')}
            />
            </div>
            <div className='pb-4'>
            <InputWithAlert
              label={t('questionnaire:tax_residence_address_label')}
              value={taxResidenceAddress}
              onChange={(e) => setTaxResidenceAddress(e.target.value)}
              disabled={!edit}
              error={inputChecked && !taxResidenceAddress}
              errorMessage={t('questionnaire:tax_residence_address_required')}
              placeholder={t('questionnaire:tax_residence_address_placeholder')}
            />
            </div>
             <div className='pb-4'>
            <InputWithAlert
              label={t('questionnaire:bank_account_number_label')}
              value={bankAccountNumber}
              onChange={(e) => setBankAccountNumber(e.target.value)}
              disabled={!edit}
              error={inputChecked && !bankAccountNumber}
              errorMessage={t('questionnaire:bank_account_number_required')}
              placeholder={t('questionnaire:bank_account_number_placeholder')}
            />
            </div>
            <div className='pb-4'>
            <InputWithAlert
              label={t('questionnaire:tax_office_label')}
              value={taxOffice}
              onChange={(e) => setTaxOffice(e.target.value)}
              disabled={!edit}
              error={inputChecked && !taxOffice}
              errorMessage={t('questionnaire:tax_office_required')}
              placeholder={t('questionnaire:tax_office_placeholder')}
            />
            </div>
            <InputWithAlert
              label={t('questionnaire:nfz_branch_label')}
              value={nfzBranch}
              onChange={(e) => setNfzBranch(e.target.value)}
              disabled={!edit}
              error={inputChecked && !nfzBranch}
              errorMessage={t('questionnaire:nfz_branch_required')}
              placeholder={t('questionnaire:nfz_branch_placeholder')}
            />
          </div>

          {/* Declarations Section */}
          <div className="space-y-4">
            <h4 className="font-semibold">{t('questionnaire:declarations_title')}</h4>

            {/* Employment Declaration */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={isEmployed}
                  onCheckedChange={setIsEmployed}
                  disabled={!edit}
                />
                <Label>{t('questionnaire:employment_declaration')}</Label>
              </div>
              
              {isEmployed && (
                <div className="ml-6 space-y-2">
                  <div className="grid grid-cols-2 gap-4">
                    <DatePicker
                      label={t('questionnaire:employment_period_from')}
                      value={employmentPeriodFrom}
                      date={employmentPeriodFrom}
                      setDate={setEmploymentPeriodFrom}
                      disabled={!edit}
                      error={getEmploymentFromError()}
                      errorMessage={t('questionnaire:employment_start_date_required')}
                      placeholder={t('questionnaire:employment_start_date_placeholder')}
                    />
                    <DatePicker
                      label={t('questionnaire:employment_period_to')}
                      value={employmentPeriodTo}
                      date={employmentPeriodTo}
                      setDate={(date) => {
                        setEmploymentPeriodTo(date);
                        if (date && employmentUnlimited) {
                          setEmploymentUnlimited(false);
                        }
                      }}
                      disabled={!edit || employmentUnlimited}
                      error={getEmploymentToError()}
                      errorMessage={t('questionnaire:employment_end_date_required')}
                      placeholder={t('questionnaire:employment_end_date_placeholder')}
                    />
                  </div>
                  <div className="flex items-center space-x-2 pt-2 pb-2">
                    <Checkbox
                      checked={employmentUnlimited}
                      onCheckedChange={(checked) => {
                        setEmploymentUnlimited(checked);
                        if (checked) {
                          setEmploymentPeriodTo(null);
                        }
                      }}
                      disabled={!edit}
                    />
                    <Label>{t('questionnaire:employment_unlimited')}</Label>
                  </div>
                  <div className='pb-4'>
                  <InputWithAlert
                    label={t('questionnaire:salary_level_label')}
                    value={salaryLevel}
                    onChange={(e) => setSalaryLevel(e.target.value)}
                    disabled={!edit}
                    placeholder={t('questionnaire:salary_level_placeholder')}
                  />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      checked={onLeave}
                      onCheckedChange={setOnLeave}
                      disabled={!edit}
                    />
                    <Label>{t('questionnaire:on_leave')}</Label>
                  </div>
                  {onLeave && (
                    <div className="grid grid-cols-2 gap-4 pt-4">
                      <DatePicker
                        label={t('questionnaire:leave_period_from')}
                        value={leavePeriodFrom}
                        date={leavePeriodFrom}
                        setDate={setLeavePeriodFrom}
                        disabled={!edit}
                        placeholder={t('questionnaire:leave_start_placeholder')}
                      />
                      <DatePicker
                        label={t('questionnaire:leave_period_to')}
                        value={leavePeriodTo}
                        date={leavePeriodTo}
                        setDate={setLeavePeriodTo}
                        disabled={!edit}
                        placeholder={t('questionnaire:leave_end_placeholder')}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Other Contract Declaration */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={hasOtherContract}
                  onCheckedChange={setHasOtherContract}
                  disabled={!edit}
                />
                <Label>{t('questionnaire:other_contract_declaration')}</Label>
              </div>
              
              {hasOtherContract && (
                <div className="ml-6 space-y-2">
                  <div className="grid grid-cols-2 gap-4">
                    <DatePicker
                      label={t('questionnaire:other_contract_period_from')}
                      value={otherContractFrom}
                      date={otherContractFrom}
                      setDate={setOtherContractFrom}
                      disabled={!edit}
                      error={inputChecked && hasOtherContract && !otherContractFrom}
                      errorMessage={t('questionnaire:other_contract_start_required')}
                      placeholder={t('questionnaire:employment_start_date_placeholder')}
                    />
                    <DatePicker
                      label={t('questionnaire:other_contract_period_to')}
                      value={otherContractTo}
                      date={otherContractTo}
                      setDate={setOtherContractTo}
                      disabled={!edit}
                      error={inputChecked && hasOtherContract && !otherContractTo}
                      errorMessage={t('questionnaire:other_contract_end_required')}
                      placeholder={t('questionnaire:employment_end_date_placeholder')}
                    />
                  </div>
                  <div className="flex items-center space-x-2 py-4">
                    <Checkbox
                      checked={otherContractExceedsMinimum}
                      onCheckedChange={setOtherContractExceedsMinimum}
                      disabled={!edit}
                    />
                    <Label>{t('questionnaire:exceeds_minimum_wage')}</Label>
                  </div>
                </div>
              )}
            </div>

            {/* Other Insurance Declaration */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2 pb-2">
                <Checkbox
                  checked={hasOtherInsurance}
                  onCheckedChange={setHasOtherInsurance}
                  disabled={!edit}
                />
                <Label>{t('questionnaire:other_insurance_declaration')}</Label>
              </div>
              
              {hasOtherInsurance && (
                <div className="ml-6">
                  <InputWithAlert
                    label={t('questionnaire:insurance_title_label')}
                    value={otherInsuranceTitle}
                    onChange={(e) => setOtherInsuranceTitle(e.target.value)}
                    disabled={!edit}
                    error={inputChecked && hasOtherInsurance && !otherInsuranceTitle}
                    errorMessage={t('questionnaire:insurance_title_required')}
                    placeholder={t('questionnaire:insurance_title_placeholder')}
                  />
                </div>
              )}
            </div>

            {/* Pension Declaration */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2 pb-2">
                <Checkbox
                  checked={isPensioner}
                  onCheckedChange={setIsPensioner}
                  disabled={!edit}
                />
                <Label>{t('questionnaire:pension_declaration')}</Label>
              </div>
              
              {isPensioner && (
                <div className="ml-6">
                  <InputWithAlert
                    label={t('questionnaire:pension_decision_number_label')}
                    value={pensionDecisionNumber}
                    onChange={(e) => setPensionDecisionNumber(e.target.value)}
                    disabled={!edit}
                    error={inputChecked && isPensioner && !pensionDecisionNumber}
                    errorMessage={t('questionnaire:pension_decision_number_required')}
                    placeholder={t('questionnaire:pension_decision_number_placeholder')}
                  />
                </div>
              )}
            </div>

            {/* Disability Declaration */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2 py-2">
                <Checkbox
                  checked={hasDisability}
                  onCheckedChange={setHasDisability}
                  disabled={!edit}
                />
                <Label>{t('questionnaire:disability_declaration')}</Label>
              </div>
              
              {hasDisability && (
                <div className="ml-6 space-y-2">
                  <InputWithAlert
                    label={t('questionnaire:disability_level_label')}
                    value={disabilityLevel}
                    onChange={(e) => setDisabilityLevel(e.target.value)}
                    disabled={!edit}
                    error={inputChecked && hasDisability && !disabilityLevel}
                    errorMessage={t('questionnaire:disability_level_required')}
                    placeholder={t('questionnaire:disability_level_placeholder')}
                  />
                  <div className="grid grid-cols-2 gap-4 py-4">
                    <DatePicker
                      label={t('questionnaire:disability_validity_from')}
                      value={disabilityFrom}
                      date={disabilityFrom}
                      setDate={setDisabilityFrom}
                      disabled={!edit}
                      error={inputChecked && hasDisability && !disabilityFrom}
                      errorMessage={t('questionnaire:disability_start_required')}
                      placeholder={t('questionnaire:employment_start_date_placeholder')}
                    />
                    <DatePicker
                      label={t('questionnaire:disability_validity_to')}
                      value={disabilityTo}
                      date={disabilityTo}
                      setDate={setDisabilityTo}
                      disabled={!edit}
                      error={inputChecked && hasDisability && !disabilityTo}
                      errorMessage={t('questionnaire:disability_end_required')}
                      placeholder={t('questionnaire:employment_end_date_placeholder')}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Simple Status Declarations */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={isStudent}
                  onCheckedChange={setIsStudent}
                  disabled={!edit}
                />
                <Label>{t('questionnaire:student_status')}</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={isUnemployed}
                  onCheckedChange={setIsUnemployed}
                  disabled={!edit}
                />
                <Label>{t('questionnaire:unemployed_status')}</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={hasSocialInsurance}
                  onCheckedChange={setHasSocialInsurance}
                  disabled={!edit}
                />
                <Label>{t('questionnaire:social_insurance_status')}</Label>
              </div>
            </div>

            {/* Voluntary Insurance */}
            <div className="space-y-2">
              <h5 className="font-medium">{t('questionnaire:voluntary_insurance_title')}</h5>
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={wantsVoluntaryHealthInsurance}
                  onCheckedChange={setWantsVoluntaryHealthInsurance}
                  disabled={!edit}
                />
                <Label>{t('questionnaire:voluntary_health_insurance')}</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={wantsVoluntaryPensionInsurance}
                  onCheckedChange={setWantsVoluntaryPensionInsurance}
                  disabled={!edit}
                />
                <Label>{t('questionnaire:voluntary_pension_insurance')}</Label>
              </div>
            </div>

            {/* Tax Exemption */}
            <div className="flex items-center space-x-2">
              <Checkbox
                checked={wantsTaxExemption}
                onCheckedChange={setWantsTaxExemption}
                disabled={!edit}
              />
              <Label>{t('questionnaire:tax_exemption')}</Label>
            </div>

            {/* Tax Residency Certificate */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={hasTaxResidencyCertificate}
                  onCheckedChange={setHasTaxResidencyCertificate}
                  disabled={!edit}
                />
                <Label>{t('questionnaire:tax_residency_certificate')}</Label>
              </div>
              
              {hasTaxResidencyCertificate && (
                <div className="ml-6 pt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <DatePicker
                      label={t('questionnaire:certificate_validity_from')}
                      value={taxResidencyCertificateFrom}
                      date={taxResidencyCertificateFrom}
                      setDate={setTaxResidencyCertificateFrom}
                      disabled={!edit}
                      error={inputChecked && hasTaxResidencyCertificate && !taxResidencyCertificateFrom}
                      errorMessage={t('questionnaire:certificate_start_required')}
                      placeholder={t('questionnaire:employment_start_date_placeholder')}
                    />
                    <DatePicker
                      label={t('questionnaire:certificate_validity_to')}
                      value={taxResidencyCertificateTo}
                      date={taxResidencyCertificateTo}
                      setDate={setTaxResidencyCertificateTo}
                      disabled={!edit}
                      error={inputChecked && hasTaxResidencyCertificate && !taxResidencyCertificateTo}
                      errorMessage={t('questionnaire:certificate_end_required')}
                      placeholder={t('questionnaire:employment_end_date_placeholder')}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Author Costs */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2 py-2">
                <Checkbox
                  checked={exceedsAuthorCostLimit}
                  onCheckedChange={setExceedsAuthorCostLimit}
                  disabled={!edit}
                />
                <Label>{t('questionnaire:author_costs_exceed_limit')}</Label>
              </div>
              
              {exceedsAuthorCostLimit && (
                <div className="ml-6">
                  <InputWithAlert
                    label={t('questionnaire:author_cost_amount_label')}
                    value={authorCostAmount}
                    onChange={(e) => setAuthorCostAmount(e.target.value)}
                    disabled={!edit}
                    error={inputChecked && exceedsAuthorCostLimit && !authorCostAmount}
                    errorMessage={t('questionnaire:author_cost_amount_required')}
                    placeholder={t('questionnaire:author_cost_amount_placeholder')}
                  />
                </div>
              )}
            </div>

            {/* Data Processing Consent */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  checked={dataProcessingConsent}
                  onCheckedChange={setDataProcessingConsent}
                  disabled={!edit}
                />
                <Label>{t('questionnaire:data_processing_consent')}</Label>
              </div>
              {inputChecked && !dataProcessingConsent && (
                <p className="text-red-500 text-sm">{t('questionnaire:data_processing_consent_required')}</p>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2">
            {!edit ? (
              <Button
                onClick={() => setEdit(true)}
                className="text-blueLink bg-transparent hover:bg-transparent shadow-none"
              >
                {hasKwestionariush ? t("button_change") : t('questionnaire:fill_button')}
              </Button>
            ) : (
              <>
                <Button
                  onClick={() => {
                    setEdit(false);
                    setInputChecked(false);
                    // Reset form data from server
                    if (hasKwestionariush) {
                      populateFormData(kwestionariushData.data.data);
                    }
                  }}
                  className="text-blueLink bg-transparent hover:bg-transparent shadow-none"
                >
                  {t("button_back")}
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('questionnaire:saving') : t("button_save")}
                </Button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}